import { useEffect, useState, useMemo } from "react";
import { Col, Row, Container, Table, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";
import Loader2 from "../../../components/Loader2.jsx";
import CompanySearch from "../../../components/Search/CompanySearch.jsx";
import api from "../../../service/api.js";
import { FileUpload } from "../../../components/FileUpload.js";

export default function CreateBankTransactions() {
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [bank, setBank] = useState(null);
    const [hasLoadedInvoices, setHasLoadedInvoices] = useState(false);
    const [selectedInvoices, setSelectedInvoices] = useState({});
    const [files, setFiles] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);

    const [searchValue, setSearchValue] = useState({
        company: {
            label: null,
            value: null,
        },
    });

    const fetchCompanyInvoices = async (companyId) => {
        try {
            setLoading(true);
            setHasLoadedInvoices(false);

            const response = await api.get(
                `tms/company-unpaid-invoices/${companyId}`
            );

            setInvoices(response.data?.data ?? response.data ?? []);
            setHasLoadedInvoices(true);
        } catch (error) {
            console.error(error);
            setInvoices([]);
            setHasLoadedInvoices(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchBankTransaction = async () => {
        try {
            setLoading(true);
            const response = await api.get(`tms/bank-transaction/show/${id}`);

            if (response) {
                response.amount = Number(response.amount ?? 0); // ensure number
                setBank(response);
                setIsDisabled(false);

                // if bank transaction already has a company, set it in the search
                if (response.company) {
                    setSearchValue({
                        company: {
                            label: response.company.name,
                            value: response.company.id,
                        },
                    });
                    setIsDisabled(true);
                }
            }
        } catch (error) {
            console.error(error);
            setBank(null);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchBankTransaction();
    }, [id]);

    useEffect(() => {
        const companyId = searchValue.company?.value;

        if (!companyId) {
            setInvoices([]);
            setHasLoadedInvoices(false);
            setSelectedInvoices({});
            return;
        }

        fetchCompanyInvoices(companyId);
    }, [searchValue.company?.value]);

    const totalSelectedAmount = useMemo(() => {
        return Object.values(selectedInvoices).reduce(
            (sum, item) => sum + Number(item.amount_to_pay ?? 0),
            0
        );
    }, [selectedInvoices]);

    const remainingBankBalance = useMemo(() => {
        if (!bank) return 0;
        return Math.max(bank.remaining_amount - totalSelectedAmount, 0);
    }, [bank?.remaining_amount, totalSelectedAmount]);

    const handleSelectInvoice = (invoice) => {
        setSelectedInvoices((prev) => {
            const currentBankAmount = Number(bank?.remaining_amount ?? 0);
            if (currentBankAmount <= 0) return prev;

            const exists = prev[invoice.id];
            if (exists) {
                const updated = { ...prev };
                delete updated[invoice.id];
                return updated;
            }

            const totalSelected = Object.values(prev).reduce(
                (sum, item) => sum + Number(item.amount_to_pay || 0),
                0
            );

            const remainingBalance = currentBankAmount - totalSelected;

            return {
                ...prev,
                [invoice.id]: {
                    amount_to_pay: Math.min(
                        invoice.remaining_amount,
                        remainingBalance > 0 ? remainingBalance : 0
                    ),
                },
            };
        });
    };

    const handleAmountChange = (invoice, value) => {
        const numericValue = Number(value) || 0;

        setSelectedInvoices((prev) => {
            const currentAmount = Number(prev[invoice.id]?.amount_to_pay || 0);

            const totalExcludingCurrent =
                Object.values(prev).reduce(
                    (sum, item) => sum + Number(item.amount_to_pay || 0),
                    0
                ) - currentAmount;

            const maxAllowed = Math.min(
                invoice.remaining_amount,
                bank?.remaining_amount - totalExcludingCurrent
            );

            return {
                ...prev,
                [invoice.id]: {
                    amount_to_pay:
                        numericValue > maxAllowed
                            ? maxAllowed
                            : numericValue < 0
                            ? 0
                            : numericValue,
                },
            };
        });
    };

    const handleSubmit = async () => {
        try {
            if (!searchValue.company?.value || !bank?.id) return;

            const selected = Object.entries(selectedInvoices).map(
                ([invoiceId, data]) => ({
                    id: invoiceId,
                    amount_to_pay: data.amount_to_pay,
                })
            );

            if (selected.length === 0) return;

            const formData = new FormData();
            formData.append("company_id", searchValue.company.value);
            selected.forEach((invoice, index) => {
                formData.append(`invoices[${index}][id]`, invoice.id);
                formData.append(
                    `invoices[${index}][amount_to_pay]`,
                    invoice.amount_to_pay
                );
            });

            if (files && files.length > 0) {
                files.forEach((item) => {
                    formData.append(`attachments[]`, item.file);
                });
            }

            await api.post(`tms/bank-transaction/apply-payment/${id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            // optional: reset after submit
            setSelectedInvoices({});
            setFiles({});

            await fetchBankTransaction();
            if (searchValue.company?.value) {
                await fetchCompanyInvoices(searchValue.company.value);
            }
            
            alert("Bank transaction applied successfully");
        } catch (error) {
            console.error(error);
            alert("Error applying bank transaction");
        }
    };

    return (
        <Container fluid>
            <Row>
                <Col md={12}>
                    <h3 className="m-0 p-0 fw-bolder">
                        Bank Transaction - Invoice Payment
                    </h3>
                </Col>

                <Col md={12} className="mt-4">
                    <CompanySearch
                        isDisabled={isDisabled}
                        value={searchValue?.company}
                        onChange={(option) =>
                            setSearchValue((prev) => ({
                                ...prev,
                                company: option,
                            }))
                        }
                    />
                </Col>
            </Row>

            <Row className="mt-4">
                <Col md={12}>
                    {loading && <Loader2 />}

                    <div className="mt-3">
                        <strong>Bank Amount:</strong> {bank?.amount}
                        <br />
                        <strong>Total Selected:</strong> {totalSelectedAmount}
                        <br />
                        <strong>Remaining Bank Balance:</strong>{" "}
                        {remainingBankBalance}
                    </div>

                    {!loading && hasLoadedInvoices && invoices.length > 0 && (
                        <>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Invoice No</th>
                                        <th>Total Amount</th>
                                        <th>Remaining</th>
                                        <th>Amount To Pay</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((invoice) => {
                                        const isSelected =
                                            !!selectedInvoices[invoice.id];
                                        const selectedData =
                                            selectedInvoices[invoice.id];

                                        return (
                                            <tr key={invoice.id}>
                                                <td>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() =>
                                                            handleSelectInvoice(
                                                                invoice
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>{invoice.number}</td>
                                                <td>{invoice.total_amount}</td>
                                                <td>
                                                    {invoice.remaining_amount}
                                                </td>
                                                <td>
                                                    {isSelected && (
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            value={
                                                                selectedData.amount_to_pay
                                                            }
                                                            onChange={(e) =>
                                                                handleAmountChange(
                                                                    invoice,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            <Row>
                                <Col md={12}>Upload</Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <FileUpload
                                            files={null}
                                            setFiles={setFiles}
                                            allowMultiple={true}
                                            maxFiles={null}
                                            className="fileupload-bg"
                                        />
                                    </Form.Group>
                                </Col>

                                {/* ✅ Submit Button */}
                                <Col md={12} className="mt-3">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSubmit}
                                        disabled={
                                            Object.keys(selectedInvoices)
                                                .length === 0
                                        }
                                    >
                                        Submit
                                    </button>
                                </Col>
                            </Row>
                        </>
                    )}

                    {!loading && hasLoadedInvoices && invoices.length === 0 && (
                        <div>No unpaid invoices found.</div>
                    )}
                </Col>
            </Row>
        </Container>
    );
}
